"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Job, JobProfile, Facility } from "../types";
import {
  facilityTypes,
  workingHours,
  contractTypes,
  benefits,
  requirements
} from "../data";
import { useToast } from "@/hooks/use-toast";
import { supabase, getAllTalents } from "@/lib/supabase";
import { useAuth } from "@/features/auth/context/AuthContext";
import { Loading } from "@/components/ui/loading";

interface JobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentJob?: Job; // Job to edit (undefined for create mode)
  title: string;
  jobProfiles: JobProfile[];
  companyId: string | null;
  onJobSaved?: () => void;
  facilities: Facility[];
}

/**
 * JobModal Component
 *
 * This modal handles both creating new jobs and editing existing jobs.
 *
 * MODES:
 * - CREATE MODE: When currentJob is undefined/null
 *   - Shows empty form fields
 *   - Displays job profile selector for pre-filling fields
 *   - Creates new job in database on save
 *
 * - EDIT MODE: When currentJob is provided
 *   - Pre-fills form fields with existing job data
 *   - Hides job profile selector (fields already populated)
 *   - Updates existing job in database on save
 */
export function JobModal({
  open,
  onOpenChange,
  currentJob,
  title,
  jobProfiles,
  companyId,
  onJobSaved,
  facilities
}: JobModalProps) {
  // No longer using context - all data comes from props
  const { toast } = useToast();
  const { profile } = useAuth();
  const [titleValue, setTitleValue] = useState("");
  const [companyValue, setCompanyValue] = useState("");
  const [locationValue, setLocationValue] = useState("");
  const [selectedWorkingHours, setSelectedWorkingHours] = useState<string>("");
  const [selectedFacilityType, setSelectedFacilityType] = useState<string>("");
  const [selectedContractType, setSelectedContractType] = useState<string>("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>(
    []
  );
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
  const [salaryMinValue, setSalaryMinValue] = useState<string>("");
  const [salaryMaxValue, setSalaryMaxValue] = useState<string>("");
  const [isActiveValue, setIsActiveValue] = useState(true);
  const [selectedJobProfile, setSelectedJobProfile] = useState<string>("");
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>("");
  const [selectedAvailableToTalents, setSelectedAvailableToTalents] = useState<
    string[]
  >([]);
  const [availableTalents, setAvailableTalents] = useState<
    { id: string; given_name: string; family_name: string; email: string }[]
  >([]);
  const [isLoadingTalents, setIsLoadingTalents] = useState(false);

  // Initialize form fields based on current mode
  useEffect(() => {
    if (currentJob) {
      // EDIT MODE: Pre-fill form with existing job data
      setTitleValue(currentJob.title || "");
      setCompanyValue(currentJob.company || "");
      setLocationValue(currentJob.location || "");
      setSelectedFacilityId(currentJob.facility || "");
      setSelectedWorkingHours(currentJob.working_hours || "");
      setSelectedFacilityType(currentJob.facility_type || "");
      setSelectedContractType(currentJob.contract_type || "");
      setDescriptionValue(currentJob.description || "");
      setSelectedRequirements(currentJob.requirements || []);
      setSelectedBenefits(currentJob.benefits || []);
      setSalaryMinValue(currentJob.salary_min?.toString() || "");
      setSalaryMaxValue(currentJob.salary_max?.toString() || "");
      setIsActiveValue(currentJob.is_active);
      setSelectedAvailableToTalents(currentJob.available_to_talents || []);
      setSelectedJobProfile(""); // Reset job profile selection
    } else {
      // CREATE MODE: Initialize with empty/default values
      setTitleValue("");
      setCompanyValue("");
      setLocationValue("");
      setSelectedFacilityId("");
      setSelectedWorkingHours("");
      setSelectedFacilityType("");
      setSelectedContractType("");
      setDescriptionValue("");
      setSelectedRequirements([]);
      setSelectedBenefits([]);
      setSalaryMinValue("");
      setSalaryMaxValue("");
      setIsActiveValue(true); // Default to active for new jobs
      setSelectedAvailableToTalents([]);
      setSelectedJobProfile("");
    }
  }, [currentJob, open]);

  // Fetch all talents if superuser
  useEffect(() => {
    if (open && profile?.isSuperUser && availableTalents.length === 0) {
      const fetchTalents = async () => {
        setIsLoadingTalents(true);
        try {
          const data = await getAllTalents();
          setAvailableTalents(data);
        } catch (error) {
          console.error("Failed to fetch available talents", error);
          toast({
            title: "Fehler",
            description: "Fehler beim Laden der Talente",
            variant: "destructive"
          });
        } finally {
          setIsLoadingTalents(false);
        }
      };
      fetchTalents();
    }
  }, [open, profile?.isSuperUser, availableTalents.length, toast]);

  // Handle job profile selection (CREATE MODE only)
  // This allows users to pre-fill form fields based on predefined job profiles
  const handleJobProfileSelect = (profileId: string) => {
    const profile = jobProfiles.find((p) => p.id === profileId);
    if (profile) {
      // Pre-fill fields based on selected job profile
      setSelectedRequirements(profile.language_levels || []);
      // You can add more mappings here based on your business logic
      setSelectedJobProfile(profileId);
    }
  };

  // Save job data to database (handles both create and update operations)
  const handleJobSave = useCallback(
    async (
      jobData: Omit<Job, "id" | "company_id" | "created_at" | "updated_at">
    ) => {
      try {
        if (currentJob) {
          // EDIT MODE: Update existing job in database
          const { error } = await supabase
            .from("jobs")
            .update({
              ...jobData
            })
            .eq("id", currentJob.id);

          if (error) throw error;
          toast({
            title: "Erfolg",
            description: "Stelle erfolgreich aktualisiert"
          });
        } else {
          // CREATE MODE: Insert new job into database
          const { error } = await supabase.from("jobs").insert({
            ...jobData,
            company_id: companyId
          });

          if (error) throw error;
          toast({
            title: "Erfolg",
            description: "Stelle erfolgreich erstellt"
          });
        }

        // Call the callback to refresh data in parent component
        if (onJobSaved) {
          onJobSaved();
        }
        onOpenChange(false);
      } catch (error) {
        console.error("Error saving job:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Speichern der Stelle",
          variant: "destructive"
        });
      }
    },
    [currentJob, companyId, onJobSaved, onOpenChange, toast]
  );

  const handleSave = () => {
    handleJobSave({
      title: titleValue,
      company: companyValue,
      location: locationValue,
      facility: selectedFacilityId || undefined,
      working_hours: selectedWorkingHours,
      facility_type: selectedFacilityType,
      contract_type: selectedContractType || undefined,
      description: descriptionValue,
      requirements: selectedRequirements,
      benefits: selectedBenefits,
      salary_min: salaryMinValue ? parseInt(salaryMinValue) : undefined,
      salary_max: salaryMaxValue ? parseInt(salaryMaxValue) : undefined,
      is_active: isActiveValue,
      available_to_talents: selectedAvailableToTalents
    });
    onOpenChange(false);
  };

  const isFormValid = () => {
    return (
      titleValue.trim() !== "" &&
      companyValue.trim() !== "" &&
      locationValue.trim() !== "" &&
      selectedWorkingHours !== "" &&
      selectedFacilityType !== "" &&
      descriptionValue.trim() !== ""
    );
  };

  const getFacilityOptionLabel = (facility: Facility) => {
    // Check if there are other facilities with same location
    const duplicates = facilities.filter(
      (f) => f.location === facility.location
    );
    let label = facility.location;

    if (duplicates.length > 1) {
      // Append address if duplicate location
      const address =
        `${facility.street || ""} ${facility.street_number || ""}`.trim();
      if (address) {
        label += ` (${address})`;
      } else if (facility.city) {
        label += ` (${facility.city})`;
      }
    }

    // Truncate if too long
    const maxLength = 60;
    if (label.length > maxLength) {
      label = `${label.substring(0, maxLength)}...`;
    }

    return label;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {currentJob
              ? "Bearbeite die Stelleninformationen"
              : "Füge eine neue Stelle hinzu"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {/* 
            CONDITIONAL RENDERING BASED ON MODE:
            - CREATE MODE (currentJob is undefined): Show job profile selector
            - EDIT MODE (currentJob is provided): Hide job profile selector
          */}
          {/* Job Profile Selection - Only shown in CREATE MODE */}
          {!currentJob && jobProfiles.length > 0 && (
            <div className="grid gap-2">
              <Label htmlFor="jobProfile">Stellenprofil (optional)</Label>
              <Select
                value={selectedJobProfile}
                onValueChange={handleJobProfileSelect}
              >
                <SelectTrigger id="jobProfile">
                  <SelectValue placeholder="Stellenprofil auswählen um Felder vorzufüllen" />
                </SelectTrigger>
                <SelectContent>
                  {jobProfiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid gap-2">
            <Label htmlFor="title">Stellentitel *</Label>
            <Input
              id="title"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              placeholder="z.B. Pflegefachkraft (m/w/d)"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="company">Unternehmen *</Label>
            <Input
              id="company"
              value={companyValue}
              onChange={(e) => setCompanyValue(e.target.value)}
              placeholder="Unternehmensname"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="location">Standort *</Label>
            <Input
              id="location"
              value={locationValue}
              onChange={(e) => setLocationValue(e.target.value)}
              placeholder="z.B. Berlin, Hamburg"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="facility">Einrichtungen</Label>
            <Select
              value={selectedFacilityId}
              onValueChange={setSelectedFacilityId}
            >
              <SelectTrigger id="facility">
                <SelectValue placeholder="Einrichtung auswählen" />
              </SelectTrigger>
              <SelectContent>
                {facilities.map((facility) => (
                  <SelectItem key={facility.id} value={facility.id}>
                    {getFacilityOptionLabel(facility)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Job Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="workingHours">Arbeitszeit *</Label>
              <Select
                value={selectedWorkingHours}
                onValueChange={setSelectedWorkingHours}
              >
                <SelectTrigger id="workingHours">
                  <SelectValue placeholder="Arbeitszeit auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {workingHours.map((hours) => (
                    <SelectItem key={hours.value} value={hours.value}>
                      {hours.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="facilityType">Einrichtungstyp *</Label>
              <Select
                value={selectedFacilityType}
                onValueChange={setSelectedFacilityType}
              >
                <SelectTrigger id="facilityType">
                  <SelectValue placeholder="Einrichtungstyp auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {facilityTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="contractType">Vertragstyp</Label>
            <Select
              value={selectedContractType}
              onValueChange={setSelectedContractType}
            >
              <SelectTrigger id="contractType">
                <SelectValue placeholder="Vertragstyp auswählen" />
              </SelectTrigger>
              <SelectContent>
                {contractTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Salary Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="salaryMin">Mindestgehalt (€/Jahr)</Label>
              <Input
                id="salaryMin"
                type="number"
                value={salaryMinValue}
                onChange={(e) => setSalaryMinValue(e.target.value)}
                placeholder="z.B. 35000"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="salaryMax">Maximalgehalt (€/Jahr)</Label>
              <Input
                id="salaryMax"
                type="number"
                value={salaryMaxValue}
                onChange={(e) => setSalaryMaxValue(e.target.value)}
                placeholder="z.B. 45000"
              />
            </div>
          </div>

          {/* Description */}
          <div className="grid gap-2">
            <Label htmlFor="description">Beschreibung *</Label>
            <Textarea
              id="description"
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              placeholder="Beschreiben Sie die Stelle und die Aufgaben..."
              rows={4}
            />
          </div>

          {/* Requirements and Benefits */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="requirements">Anforderungen</Label>
              <MultiSelect
                options={requirements}
                selectedValues={selectedRequirements}
                onValueChange={setSelectedRequirements}
                placeholder="Anforderungen auswählen"
                maxCount={10}
                modalPopover={true}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="benefits">Vorteile</Label>
              <MultiSelect
                options={benefits}
                selectedValues={selectedBenefits}
                onValueChange={setSelectedBenefits}
                placeholder="Vorteile auswählen"
                maxCount={10}
                modalPopover={true}
              />
            </div>
          </div>

          {/* Available to Talents (Superuser Only) */}
          {profile?.isSuperUser && (
            <div className="grid gap-2">
              <Label htmlFor="availableToTalents">
                Verfügbar für Talente (Optional)
              </Label>
              {isLoadingTalents ? (
                <div className="flex items-center justify-center p-2 border rounded-md h-10">
                  <Loading size="sm" />
                </div>
              ) : (
                <MultiSelect
                  options={availableTalents.map((talent) => ({
                    value: talent.id,
                    label: `${talent.given_name} ${talent.family_name} (${talent.email})`
                  }))}
                  selectedValues={selectedAvailableToTalents}
                  onValueChange={setSelectedAvailableToTalents}
                  placeholder="Talente auswählen"
                  modalPopover={true}
                />
              )}
            </div>
          )}
          {/* Active Status */}
          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActiveValue}
              onCheckedChange={setIsActiveValue}
            />
            <Label htmlFor="isActive">Stelle ist aktiv</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid()}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
